// Client side of the self-hosted proof-of-work captcha (Altcha-compatible).
// Fetches a challenge from our API proxy, brute-forces the hidden number with
// Web Crypto, and returns the base64 payload the server verifies. No external
// service, no account, no cookies.

export interface Challenge {
  algorithm: string;
  challenge: string;
  salt: string;
  signature: string;
  maxnumber: number;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function solve(c: Challenge): Promise<string> {
  for (let n = 0; n <= c.maxnumber; n++) {
    if ((await sha256Hex(c.salt + n)) === c.challenge) {
      const payload = {
        algorithm: c.algorithm,
        challenge: c.challenge,
        number: n,
        salt: c.salt,
        signature: c.signature,
      };
      return btoa(JSON.stringify(payload));
    }
  }
  throw new Error("Captcha unsolved");
}

/** Fetch a fresh challenge and return a solved token ready to submit. */
export async function getCaptchaToken(): Promise<string> {
  const res = await fetch("/api/captcha/challenge", { cache: "no-store" });
  if (!res.ok) throw new Error("Captcha challenge failed");
  const challenge = (await res.json()) as Challenge;
  return solve(challenge);
}
